<template>
  <div class="invoice-viewer" v-show="(loading || (isPdf && pdfInstance))">
    <transition name="slow-hide">
      <div v-if="loading" class="loading-container center animated-element">
        <div class="text-center">
          <h5>{{$t('app.loading')}}</h5>
          <i class="fad fa-spinner-third fa-spin"></i>
        </div>
      </div>
    </transition>
    <div v-if="isPdf && pdfInstance && !loading" class="pdf-toolbar">
      <a @click="prevPage()" class="btn btn-outline-secondary btn-sm" href="javascript:"><i
          class="fal fa-angle-left"></i></a>
      <a class="btn btn-outline-secondary btn-sm" href="javascript:">
        {{currentPage}}
      </a>
      <a @click="nextPage()" class="btn btn-outline-secondary btn-sm" href="javascript:"><i
          class="fal fa-angle-right"></i></a>
      <a @click="zoomOut()" class="btn btn-outline-secondary btn-sm" href="javascript:"><i
          class="fal fa-search-minus"></i></a>
      <a @click="zoomIn()" class="btn btn-outline-secondary btn-sm" href="javascript:"><i
          class="fal fa-search-plus"></i></a>
      <a @click="download()" class="btn btn-outline-secondary btn-sm" href="javascript:"><i
          class="fal fa-download"></i></a>
    </div>
    <div v-show="isPdf && !loading" class="pdf-viewer-container">
      <canvas class="pdf-viewer"></canvas>
    </div>
  </div>

</template>

<script>
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import * as pdfWorker from 'pdfjs-dist/build/pdf.worker.entry';
import { mapState } from 'vuex';
import InvoiceForm from '@/portal-components/invoice/invoice-form';
import InvoiceItem from './invoice-item';

export default {
  name: 'InvoiceViewer',
  components: {
    InvoiceItem,
    InvoiceForm,
  },
  props: {
    src: String,
    loading: Boolean,
    activeInvoice: Object,
    scale: {
      type: Number,
      default: 1.5,
    },
    hideFormViewer: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isPdf: true,
      currentPage: 1,
      pdfInstance: null,
    };
  },
  mounted() {
    this.isPdf = this.src.indexOf('application/pdf') > -1;
    this.render();
  },
  computed: {
    ...mapState([
      'invoiceList',
    ]),
    invoiceModel() {
      if (!this.activeInvoice || !this.activeInvoice.id || this.invoiceList.length === 0) {
        return null;
      }
      const inv = this.invoiceList.filter(i => i.id === this.activeInvoice.id);
      return inv.length > 0 ? inv[0] : null;
    },
    base64RawContent() {
      this.isPdf = this.src.indexOf('application/pdf') > -1;
      return this.src.substr(28);
    },
  },
  methods: {
    prevPage() {
      if (this.currentPage <= 1) {
        return;
      }
      this.currentPage--;
      this.displayPage(this.pdfInstance, this.currentPage);
    },
    nextPage() {
      if (this.currentPage >= this.pdfInstance.numPages) {
        return;
      }
      this.currentPage++;
      this.displayPage(this.pdfInstance, this.currentPage);
    },
    zoomIn() {
      if (this.pdfScale > 1.6) {
        return;
      }
      this.pdfScale = this.pdfScale + 0.1;
      this.displayPage(this.pdfInstance, this.currentPage);
    },
    zoomOut() {
      if (this.pdfScale < 0.7) {
        return;
      }
      this.pdfScale = this.pdfScale - 0.1;
      this.displayPage(this.pdfInstance, this.currentPage);
    },
    renderPage(page) {
      const scale = this.pdfScale; // render with global pdfScale variable
      const viewport = page.getViewport({ scale });
      const canvas = this.$el.querySelector('.pdf-viewer');
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      const renderContext = {
        canvasContext: context,
        viewport,
      };
      page.render(renderContext);
    },
    displayPage(pdf, num) {
      pdf.getPage(num).then((page) => { this.renderPage(page); });
    },
    download() {
      const downloadLink = document.createElement('a');
      const fileName = 'invoice.pdf';
      downloadLink.href = this.src;
      downloadLink.download = fileName;
      downloadLink.click();
    },
    downloadXml() {
      const element = document.createElement('a');
      element.setAttribute('href', `data:text/plain;charset=utf-8,${encodeURIComponent(this.base64RawContent)}`);
      element.setAttribute('download', 'invoice.xml');
      element.style.display = 'none';
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    },
    render() {
      if (!this.base64RawContent || !this.isPdf) {
        return;
      }
      this.pdfScale = this.scale;
      pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;
      // Using DocumentInitParameters object to load binary data.
      const loadingTask = pdfjsLib.getDocument({ data: atob(this.base64RawContent) });
      loadingTask.promise.then((pdf) => {
        this.pdfInstance = pdf;
        this.displayPage(pdf, this.currentPage);
      }, () => {
        // PDF loading error
        // console.error(error);
      });
    },
  },
  watch: {
    base64RawContent() {
      if (this.base64RawContent) {
        this.render();
      }
    },
  },
};
</script>
<style scoped>
.invoice-viewer {
  height: 100%;
}


.pdf-toolbar, .pdf-viewer, .invoice-viewer {
  text-align: center;
}
.pdf-toolbar, .pdf-toolbar a  {
  display: flex;
  justify-content: center;
  align-items: center;
}
.pdf-toolbar a {
  font-weight: bold;
  width: 1.75rem;
  height: 1.75rem;
  margin: 8px;
}
.invoice-viewer {
  height: 100%;
  background: white;
  border-radius: 6px;
}
.pdf-viewer-container {
  overflow: auto;
  height: calc(100% - 50px);
}

@media only screen and (max-width: 1500px) {
  .pdf-viewer-container {
    max-width: 750px;
    overflow-x: scroll;
  }
}

@media only screen and (max-width: 1400px) {
  .pdf-viewer-container {
    max-width: 700px;
    overflow-x: scroll;
  }
}

@media only screen and (max-width: 1300px) {
  .pdf-viewer-container {
    max-width: 600px;
    overflow-x: scroll;
  }
}
</style>



// WEBPACK FOOTER //
// src/portal-components/invoice/invoice-viewer.vue